import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { BUTTON_TITLE, LOCALES } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import { LoadingStatus, TBooleanInNumber } from 'src/types/main';
import { confirmDelete, confirmMessage, leaveWithoutSaving } from 'src/utils/modalConfirmations';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Input from 'src/view/components/Controls/Input/Input';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import Text, { TEXT } from 'src/view/components/Text/Text';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
// eslint-disable-next-line
import MultiLanguageCountrySearchBox from 'src/view/screens/SettingsScreen/screens/General/LanguageCountryCurrency/components/CountrySearchBox/MultiLanguageCountrySearchBox';
import { ICountrySearchResult } from 'src/view/screens/SettingsScreen/screens/General/LanguageCountryCurrency/types/countries';
import countriesStore from 'src/view/screens/SettingsScreen/store/countriesStore';
import { getTranslate } from '~/utils/helpers';
import { removeToast, toastError } from '~/utils/toasts';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import './style.scss';

const toastErrorId: string = 'country-toast-error-id';

type TProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const UpdateOrCreateCountry: React.FC<TProps> = observer((props: TProps): JSX.Element => {
  useEffect(() => {
    if (props.match.params.id) {
      countriesStore.getCountry(props.match.params.id);
      return;
    }
    countriesStore.setCountry();
  }, []);

  useEffect(() => {
    if (!countriesStore.country?.id || countriesStore.countryCreateLoadingStatus !== LoadingStatus.Success) {
      return;
    }
    props.history.replace(SCREENS.SETTINGS_COUNTRIES());
  }, [countriesStore.countryCreateLoadingStatus]);

  useEffect(() => {
    if (countriesStore.countryDeleteLoadingStatus === LoadingStatus.Success) {
      props.history.replace(SCREENS.SETTINGS_COUNTRIES());
    }
  }, [countriesStore.countryDeleteLoadingStatus]);

  useEffect(() => {
    if (!countriesStore.country?.id || countriesStore.country?.id === 0 || countriesStore.countryUpdateLoadingStatus !== LoadingStatus.Success) {
      return;
    }
    props.history.replace(SCREENS.SETTINGS_COUNTRIES());
  }, [countriesStore.countryUpdateLoadingStatus]);

  const isLoading = useMemo((): boolean => {
    const loadingStatuses = [LoadingStatus.Loading, LoadingStatus.Init, LoadingStatus.Refreshing];
    return loadingStatuses.includes(countriesStore.countryLoadingStatus);
  }, [countriesStore.countryLoadingStatus]);

  const hasErrors = useMemo((): boolean => {
    return !countriesStore.isValid;
  }, [countriesStore.validation]);

  const goBack = () => {
    if (!countriesStore.edited) {
      props.history.push(SCREENS.SETTINGS_COUNTRIES());
      return;
    }
    leaveWithoutSaving({
      title: localeStore.t('title_leave_without_changes'),
      message: localeStore.t('leave_without_changes_desc'),
      onConfirm: () => props.history.push(SCREENS.SETTINGS_COUNTRIES()),
    });
  };

  const deleteCountry = () => {
    if (!countriesStore.country) {
      return;
    }
    confirmDelete({
      title: localeStore.t('delete_country_header'),
      message: localeStore.t('txt_country_will_be_deleted'),
      onConfirm: () => countriesStore.country && countriesStore.deleteCountry(countriesStore.country.id),
      redConfirmBtn: true,
    });
  };

  const onStatusChanged = (status: TBooleanInNumber) => {
    if (status) removeToast(toastErrorId);
    countriesStore.changeCountryHandler({ status });
  };

  const onDefaultChanged = (isDefault: TBooleanInNumber) => {
    if (isDefault) removeToast(toastErrorId);
    countriesStore.changeCountryHandler({ is_default: isDefault });
  };

  const onSave = () => {
    if (countriesStore.originIsDefault && (!countriesStore.country?.status || !countriesStore.country?.is_default)) {
      toastError({ id: toastErrorId, message: localeStore.t('disable_default_country') });
      return;
    }

    if (countriesStore.country?.is_default && (!countriesStore.country?.id || !countriesStore.originIsDefault)) {
      confirmMessage({
        title: localeStore.t('set_default_country_header'),
        message: localeStore.t('txt_set_country_as_default', { country_name: getTranslate(countriesStore.country?.name) }),
        onConfirm: saveCountry,
        onCancel: () => countriesStore.changeCountryHandler({ is_default: 0 }),
        confirmBtnTxt: localeStore.t(BUTTON_TITLE.SET_DEFAULT),
        cancelBtnTxt: localeStore.t(BUTTON_TITLE.CANCEL),
      });
    } else {
      saveCountry();
    }
  };

  const onCountryCodeChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    let val: string | null | number = event.target.value;
    if (!val || val.length === 0) {
      val = null;
    } else {
      val = Number(val);
    }
    countriesStore.changeCountryHandler({ numeric_code: val });
  };

  const saveCountry = () => {
    if (countriesStore.country?.id) {
      countriesStore.updateCountry();
      return;
    }
    countriesStore.createCountry();
  };

  const onSelectCountry = (result: ICountrySearchResult) => {
    countriesStore.changeCountryHandler({
      name: {
        ...countriesStore.country?.name,
        [result.locale]: result.name,
      },
      place_id: result.placeId,
    });
  };

  return (
    <div className="d-flex flex-column full-width update-or-create-country">
      <Headers>
        <HeaderBack
          title={props.match.params.id ? 'update_country_page' : 'create_country_page'}
          callback={goBack}
          path={SCREENS.SETTINGS_COUNTRIES()}
        />
      </Headers>
      <div className="d-flex flex-column flex-grow-1 pt-6">
        {!isLoading ? (
          <div className="d-flex align-items-center">
            {/* country status */}
            <div className="d-flex flex-row align-items-center mb-3 mr-12">
              <Switcher status={countriesStore.country?.status || 0} onChange={onStatusChanged} />
              <Text type={TEXT.CUSTOM} text="status" className="update-or-create-country__switcher-label" />
            </div>
            {/* country is default */}
            <div className="d-flex flex-row align-items-center mb-3">
              <Switcher status={countriesStore.country?.is_default || 0} onChange={onDefaultChanged} />
              <Text type={TEXT.CUSTOM} text="txt_set_as_default" className="update-or-create-country__switcher-label" />
            </div>
          </div>
        ) : (
          <Skeleton width={100} height={46} style={{ borderRadius: 12 }} />
        )}
        {/* country name */}
        <div className="update-or-create-country__input-wrapper">
          <Text type={TEXT.CUSTOM} text="country" className="update-or-create-country__input-label" required />
          {!isLoading ? (
            <>
              <MultiLanguageCountrySearchBox
                enValue={countriesStore.country?.name.en}
                arValue={countriesStore.country?.name.ar}
                onSelect={onSelectCountry}
              />
              {(!countriesStore.validation.name[LOCALES.EN].state || !countriesStore.validation.name[LOCALES.AR].state) &&
              (countriesStore.validation.name[LOCALES.EN].errorText || countriesStore.validation.name[LOCALES.AR].errorText) ? (
                <div className="input-error-message">
                  {countriesStore.validation.name[LOCALES.EN].errorText || countriesStore.validation.name[LOCALES.AR].errorText}
                </div>
              ) : null}
            </>
          ) : (
            <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
          )}
        </div>
        {/* country numeric code */}
        <div className="update-or-create-country__input-wrapper">
          <div className="d-flex align-items-center mb-2">
            <Text
              type={TEXT.CUSTOM}
              text="numeric_code"
              className="update-or-create-country__input-label mb-0"
              required
              tooltip={{ message: 'numeric_code_info', position: TOOLTIP_POSITION.FIXED, duration: TOOLTIP_DURATION.EXTRA_LARGE }}
            />
          </div>
          {!isLoading ? (
            <Input
              wrapperClassName="update-or-create-country__input"
              error={!countriesStore.validation.numeric_code.state}
              onChange={onCountryCodeChanged}
              placeholder={localeStore.t('numeric_code')}
              value={!countriesStore.country?.numeric_code && countriesStore.country?.numeric_code !== 0 ? '' : countriesStore.country?.numeric_code}
              className="input-mini family-medium text-elipsis"
              errorMessage={countriesStore.validation.numeric_code.errorText || ''}
            />
          ) : (
            <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
          )}
        </div>
        {/* country alpha 2 code */}
        <div className="update-or-create-country__input-wrapper">
          <div className="d-flex align-items-center mb-2">
            <Text
              type={TEXT.CUSTOM}
              text="alfa_2_code"
              className="update-or-create-country__input-label mb-0"
              required
              tooltip={{ message: 'alfa_2_code_info', position: TOOLTIP_POSITION.FIXED, duration: TOOLTIP_DURATION.EXTRA_LARGE }}
            />
          </div>
          {!isLoading ? (
            <Input
              wrapperClassName="update-or-create-country__input"
              error={!countriesStore.validation.alfa_2_code.state}
              onChange={(e) => countriesStore.changeCountryHandler({ alfa_2_code: e.target.value })}
              placeholder={localeStore.t('alfa_2_code')}
              value={countriesStore.country?.alfa_2_code}
              className="input-mini family-medium text-elipsis"
              errorMessage={countriesStore.validation.alfa_2_code.errorText || ''}
            />
          ) : (
            <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
          )}
        </div>
        {/* country isd code */}
        <div className="update-or-create-country__input-wrapper">
          <div className="d-flex align-items-center mb-2">
            <Text
              type={TEXT.CUSTOM}
              text="isd_code"
              className="update-or-create-country__input-label mb-0"
              required
              tooltip={{ message: 'isd_code_info', position: TOOLTIP_POSITION.FIXED, duration: TOOLTIP_DURATION.EXTRA_LARGE }}
            />
          </div>
          {!isLoading ? (
            <Input
              wrapperClassName="update-or-create-country__input"
              error={!countriesStore.validation.isd_code.state}
              onChange={(e) => countriesStore.changeCountryHandler({ isd_code: e.target.value })}
              placeholder={localeStore.t('isd_code')}
              value={countriesStore.country?.isd_code}
              className="input-mini family-medium text-elipsis"
              errorMessage={countriesStore.validation.isd_code.errorText || ''}
            />
          ) : (
            <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
          )}
        </div>
        <div className="d-flex align-items-center mt-3 gap-10">
          <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={onSave} disabled={hasErrors} loading={isLoading} />
          {!isLoading && !countriesStore.originIsDefault && countriesStore.country?.id !== 0 && (
            <Button
              title={localeStore.t(BUTTON_TITLE.DELETE)}
              onClick={deleteCountry}
              disabled={hasErrors}
              loading={isLoading}
              appearance="tertiary"
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default UpdateOrCreateCountry;
